import type { SortingState, Updater } from "@tanstack/react-table"
import type { RefObject } from "react"
import { useShallow } from "zustand/react/shallow"

import type { ProductInventoriesSearchParams } from "../schemas"
import { useProductInventorySearchStore } from "../stores"

import type { InventoryByProductItem } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"

interface UseProductInventoryListReturn {
  inventories: InventoryByProductItem[]
  isFetching: boolean
  error: Error | undefined
  sorting: SortingState
  searchParams: ProductInventoriesSearchParams
  tableContainerRef: RefObject<HTMLDivElement>
  handleSortingChange: (updater: Updater<SortingState>) => void
  handleSearchChange: (query: string) => void
  handleFilterChange: <K extends keyof ProductInventoriesSearchParams>(
    key: K,
    value: ProductInventoriesSearchParams[K]
  ) => void
  handleFilterRemove: (key: keyof ProductInventoriesSearchParams, value?: string | number) => void
  handleFilterClear: () => void
  handleScroll: () => void
}

const ROUTE = "/app/inventory/_layout/products"

const FILTER_KEYS: (keyof ProductInventoriesSearchParams)[] = [
  "productStatuses",
  "inventoryStatuses",
  "productManagerIds",
  "productClassIds",
]

const PRESERVED_EMPTY_FILTER_KEYS: (keyof ProductInventoriesSearchParams)[] = ["productStatuses"]

export const useProductInventoryList = (): UseProductInventoryListReturn => {
  const store = useProductInventorySearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )

  const {
    data: inventories,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
  } = useListPageBase<InventoryByProductItem, ProductInventoriesSearchParams>({
    route: ROUTE,
    store,
    filterKeys: FILTER_KEYS,
    preservedEmptyFilterKeys: PRESERVED_EMPTY_FILTER_KEYS,
  })

  return {
    inventories,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
  }
}
